import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", planId: null });
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) { setState({ status: "error" }); return; }
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", planId: data.plan_id });
          await refresh();
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setState({ status: "failed" });
          return;
        }
      } catch { /* keep polling */ }
      if (attempts < 30) setTimeout(poll, 2000);
      else setState({ status: "timeout" });
    };
    poll();
  }, [sessionId, refresh]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center" data-testid="billing-success">
      <Card className="p-10 max-w-md w-full text-center" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
        {state.status === "polling" && (
          <>
            <Loader2 className="w-14 h-14 mx-auto animate-spin" style={{ color: "var(--brand)" }} />
            <h2 className="display text-2xl mt-4">Confirming payment…</h2>
            <p className="text-sm mt-2" style={{ color: "var(--text-soft)" }}>Please wait, this takes a few seconds.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500" />
            <h2 className="display text-3xl mt-4">You're on {state.planId?.toUpperCase() || "Pro"} 🎉</h2>
            <p className="text-sm mt-2" style={{ color: "var(--text-soft)" }}>Your subscription is active. Enjoy the new limits.</p>
            <div className="flex gap-3 mt-6 justify-center">
              <Button asChild style={{ background: "var(--brand)" }}><Link to="/" data-testid="go-dashboard">Go to Dashboard</Link></Button>
              <Button asChild variant="outline"><Link to="/billing">View Billing</Link></Button>
            </div>
          </>
        )}
        {(state.status === "failed" || state.status === "timeout" || state.status === "error") && (
          <>
            <XCircle className="w-14 h-14 mx-auto text-red-500" />
            <h2 className="display text-2xl mt-4">Payment not confirmed</h2>
            <p className="text-sm mt-2" style={{ color: "var(--text-soft)" }}>
              {state.status === "timeout" ? "Still processing — refresh in a minute." : "Something went wrong. Please try again."}
            </p>
            <Button asChild className="mt-6" style={{ background: "var(--brand)" }}><Link to="/billing">Back to Billing</Link></Button>
          </>
        )}
      </Card>
    </div>
  );
}
